import Field from "./field";
import Entity from "./entity";
import Api from "./api";
import { getEntries, replaceAll, uniq } from "../utils";
import IRollupFilterConfig from "../interface/rollup-filter-config";
import IRollupFilter from "../interface/rollup-filter";
import IRollup from "../interface/rollup";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IApiRollupPtDetail from "../interface/api/api-rollup-pt-detail";
import IApiRollup from "../interface/api/api-rollup";
import IApiField from "../interface/api/api-field";
import RollupFilterRule from "../enum/rollup-filter-rule";
import FieldType from "../enum/field-type";

const MrkFieldId: string = '$FIELD_ID$';

const MrkValueMin: string = '$VALUE_MIN$';

const MrkValueMax: string = '$VALUE_MAX$';

const MrkAndFilter: string = ' AND ';

const WhereFix: string = MrkAndFilter + 'c0 isnull'; // backend works incorrectly when clause starts and ends with paranthesis, that's why this useless string added to the end of every rollup

const FilterRules: IRollupFilterConfig[] = [
    {
        type: FieldType.text,
        rule: RollupFilterRule.TextEmpty,
        expression: /c\d+ isnull or c\d+=''/,
        builder: `(c${MrkFieldId} isnull or c${MrkFieldId}='')`,
        valueMin: false,
        valueMax: false
    },
    {
        type: FieldType.text,
        rule: RollupFilterRule.TextEquals,
        expression: /c\d+='.*?'/,
        builder: `c${MrkFieldId}='${MrkValueMin}'`,
        valueMin: true,
        valueMax: false
    },
    {
        type: FieldType.check,
        rule: RollupFilterRule.LogicalIndeterminate,
        expression: /c\d+ isnull or c\d+ not='1' and c\d+ not='0'/,
        builder: `(c${MrkFieldId} isnull or c${MrkFieldId} not='1' and c${MrkFieldId} not='0')`,
        valueMin: false,
        valueMax: false
    },
    {
        type: FieldType.check,
        rule: RollupFilterRule.LogicalTrue,
        expression: /c\d+='1'/,
        builder: `c${MrkFieldId}='1'`,
        valueMin: false,
        valueMax: false
    },
    {
        type: FieldType.check,
        rule: RollupFilterRule.LogicalFalse,
        expression: /c\d+='0'/,
        builder: `c${MrkFieldId}='0'`,
        valueMin: false,
        valueMax: false
    },
    {
        type: FieldType.number,
        rule: RollupFilterRule.NumberBetween,
        expression: /c\d+>'\d+' and c\d+<'\d+'/,
        builder: `c${MrkFieldId}>'${MrkValueMin}' and c${MrkFieldId}<'${MrkValueMax}'`,
        valueMin: true,
        valueMax: true
    },
    {
        type: FieldType.number,
        rule: RollupFilterRule.NumberEmpty,
        expression: /c\d+ isnull or c\d+=''/,
        builder: `(c${MrkFieldId} isnull or c${MrkFieldId}='')`,
        valueMin: false,
        valueMax: false
    },
    {
        type: FieldType.number,
        rule: RollupFilterRule.NumberEquals,
        expression: /c\d+='\d+'/,
        builder: `c${MrkFieldId}='${MrkValueMin}'`,
        valueMin: true,
        valueMax: false
    },
    {
        type: FieldType.number,
        rule: RollupFilterRule.NumberMore,
        expression: /c\d+>'\d+'/,
        builder: `c${MrkFieldId}>'${MrkValueMin}'`,
        valueMin: true,
        valueMax: false
    },
    {
        type: FieldType.number,
        rule: RollupFilterRule.NumberLess,
        expression: /c\d+<'\d+'/,
        builder: `c${MrkFieldId}<'${MrkValueMin}'`,
        valueMin: true,
        valueMax: false
    },
    {
        type: FieldType.linked,
        rule: RollupFilterRule.LinkedEmpty,
        expression: /c\d+\[0\] isnull/,
        builder: `c${MrkFieldId}[0] isnull`,
        valueMin: false,
        valueMax: false
    },
    {
        type: FieldType.linked,
        rule: RollupFilterRule.LinkedEquals,
        expression: /c\d+\[0\]='\d+'/,
        builder: `c${MrkFieldId}[0]='${MrkValueMin}'`,
        valueMin: true,
        valueMax: false
    },
];

class RollupFiltersInstance {

    /* Public Methods */

    public getTypes(): FieldType[] {
        return uniq(FilterRules.map((rule: IRollupFilterConfig) => rule.type));
    }

    public setServerFilters(rollup: IRollup, raw: Partial<IApiRollup>): void {
        const pathFilters: { [entityId: number]: string[] } = {};
        rollup.filters.forEach((filter: IRollupFilter) => {
            const pathId: number[] = filter.fieldId.split(',').map((i: string) => parseInt(i));
            const fieldId: number = pathId.pop() as number;
            const field: IApiField | null = Api.getField(fieldId);
            const strFilter: string = this.getFilterString(fieldId, filter);
            if (field && strFilter) {
                if (!pathFilters[field.eCN]) {
                    pathFilters[field.eCN] = [];
                }
                pathFilters[field.eCN].push(strFilter);
            }
        });
        if (raw.rolPtDetails) {
            raw.rolPtDetails.forEach((ptDetail: IApiRollupPtDetail) => {
                ptDetail.where = '';
                if (ptDetail.ptId) {
                    const ptField: IApiField | null = Api.getField(ptDetail.ptId);
                    if (ptField && ptField.pointer) {
                        const entityId: number = ptField.pointer[0].ptEcn;
                        if (pathFilters[entityId] && pathFilters[entityId].length) {
                            ptDetail.where = pathFilters[entityId].join(MrkAndFilter);
                            /* fix for backend, watch WhereFix comment */
                            ptDetail.where = ptDetail.where + WhereFix;
                        }
                    }
                } else if (raw.rolPtDetails) {
                    if (raw.rolPtDetails.length > 1) {
                        const ptField: IApiField | null = Api.getField(raw.rolPtDetails[raw.rolPtDetails.length - 2].ptId);
                        if (ptField && ptField.pointer) {
                            const entityId: number = ptField.pointer[1].ptEcn;
                            if (pathFilters[entityId]) {
                                ptDetail.where = pathFilters[entityId].join(MrkAndFilter);
                                /* fix for backend, watch WhereFix comment */
                                ptDetail.where = ptDetail.where + WhereFix;
                            }
                        }
                    } else if (pathFilters[ptDetail.rolECN as number]) {
                        ptDetail.where = pathFilters[ptDetail.rolECN as number].join(MrkAndFilter);
                        /* fix for backend, watch WhereFix comment */
                        ptDetail.where = ptDetail.where + WhereFix;
                    }
                }
            });
        }
    }

    public parseServerFilters(raw: IApiRollup): IRollupFilter[] {
        const filters: string[] = [];
        const result: IRollupFilter[] = [];
        raw.rolPtDetails.forEach((ptDetail: IApiRollupPtDetail) => {
            if (ptDetail.where) {
                filters.push(...ptDetail.where.split(MrkAndFilter));
            }
        });
        filters.forEach((str: string) => {
            const filter: IRollupFilter | null = this.parseServerFilter(str, raw);
            if (filter) {
                result.push(filter);
            }
        });
        return result;
    }

    public getFieldRollupPath(raw: IApiRollup, fieldId: number): number[] | undefined {
        const field: IApiField | null = Api.getField(fieldId);
        if (field) {
            const result: number[] = [];
            if (raw.rolPtDetails.length) {
                const lastPtDetail: IApiRollupPtDetail = raw.rolPtDetails[raw.rolPtDetails.length - 1];
                if (lastPtDetail.rolECN !== field.eCN) {
                    for (let i = 0; i < raw.rolPtDetails.length; i++) {
                        if (raw.rolPtDetails[i].ptId) {
                            const ptField: IApiField | null = Api.getField(raw.rolPtDetails[i].ptId);
                            if (ptField && ptField.pointer) {
                                const foreignEntityId: number = ptField.pointer[1].ptEcn;
                                result.push(ptField.id);
                                if (field.eCN === foreignEntityId) {
                                    break;
                                }
                            } else {
                                return undefined;
                            }
                        } else if (raw.rolPtDetails[i].rolECN === field.eCN) {
                            break;
                        }
                    }
                }
            }
            result.push(field.id);
            return result;
        }
    }

    /* Private Methods */

    private getFilterString(fieldId: number, filter: IRollupFilter): string {
        const field: IApiField | null = Api.getField(fieldId);
        const config: IRollupFilterConfig | undefined = FilterRules.find((config: IRollupFilterConfig) => {
            return config.rule === filter.rule;
        });
        if (field && config && field.uiType === config.type) {
            if (field.uiType === FieldType.linked && field.linkedRelation) {
                fieldId = field.linkedRelation[0];
            }
            let result = `${config.builder}`;
            result = replaceAll(result, MrkFieldId, fieldId.toString());
            result = replaceAll(result, MrkValueMin, filter.valueMin ? filter.valueMin.toString() : '');
            result = replaceAll(result, MrkValueMax, filter.valueMax ? filter.valueMax.toString() : '');
            return result;
        }
        return '';
    }

    private parseServerFilter(strFilter: string, raw: IApiRollup): IRollupFilter | null {
        let result: IRollupFilter | null = null;
        let field: IField | null = null;
        getEntries(strFilter, /c\d+/).forEach((entry: string) => {
            field = Field.get(parseInt(entry.slice(1)));
        });
        if (field && (field as IField).uiType === FieldType.pointer) {
            field = this.getLinkedByPointer(field);
        }
        if (field) {
            const pathId: number[] | undefined = this.getFieldRollupPath(raw, field.id);
            if (pathId) {
                const filters: IRollupFilterConfig[] = FilterRules.filter((config: IRollupFilterConfig) => {
                    return field && config.type === field.uiType
                });
                filters.forEach((config: IRollupFilterConfig) => {
                    getEntries(strFilter, config.expression).forEach((entry: string) => {
                        result = {
                            fieldId: pathId.join(','),
                            rule: config.rule,
                            valueMin: null,
                            valueMax: null
                        };
                        if (config.valueMin || config.valueMax) {
                            if (config.valueMin) {
                                result.valueMin = this.parseServerFilterValue(config, strFilter, 0);
                            }
                            if (config.valueMax) {
                                result.valueMax = this.parseServerFilterValue(config, strFilter, 1);
                            }
                        }
                        strFilter = replaceAll(strFilter, entry, '');
                    });
                });
            }
        }
        return result;
    }

    private parseServerFilterValue(config: IRollupFilterConfig, filter: string, index: number): string | number {
        const values: string[] = getEntries(filter, /\'.*?\'/g);
        let result: string | number = replaceAll(values[index], '\'', '');
        if (config.type === FieldType.number || config.type === FieldType.linked) {
            result = Number(result);
        }
        return result;
    }

    private getLinkedByPointer(pointer: IField): IField | null {
        const entity: IEntity = Entity.get(pointer.entityId) as IEntity;
        for (let i = 0; i < entity.fields.length; i++) {
            const field: IField = entity.fields[i];
            if (field.uiType === FieldType.linked && field.linkedRelation && field.linkedRelation[0] === pointer.id) {
                return field;
            }
        }
        return null;
    }

}

const RollupFilters = new RollupFiltersInstance();

export default RollupFilters;